import ResourcesIds from '../../resources/ResourcesIds';

/**
 * The days and hours a clinic is open.
 *
 * Nothing here is nullable, for the reason `reminderSettings` gives: a clinic that has never
 * opened this screen is not unconfigured, it is open on whatever the API says it is open on.
 * The API answers with all seven days rather than nulls, so the editor has no empty state to
 * render and an admin sees what is actually happening to their diary.
 *
 * The times are the clinic's own wall clock with no zone, like every other time in this app --
 * `08:00` is eight in the morning at the clinic, whoever is looking.
 */

/** `MONDAY` .. `SUNDAY`, the names the API speaks. */
export type Weekday =
    | 'MONDAY'
    | 'TUESDAY'
    | 'WEDNESDAY'
    | 'THURSDAY'
    | 'FRIDAY'
    | 'SATURDAY'
    | 'SUNDAY';

/** Every day the editor draws, in the order it draws them. Monday first, as the API sends it. */
export const WEEKDAYS: Weekday[] = [
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
    'SUNDAY',
];

export const WEEKDAY_LABELS: Record<Weekday, string> = {
    MONDAY: ResourcesIds.WEEKDAY_MONDAY,
    TUESDAY: ResourcesIds.WEEKDAY_TUESDAY,
    WEDNESDAY: ResourcesIds.WEEKDAY_WEDNESDAY,
    THURSDAY: ResourcesIds.WEEKDAY_THURSDAY,
    FRIDAY: ResourcesIds.WEEKDAY_FRIDAY,
    SATURDAY: ResourcesIds.WEEKDAY_SATURDAY,
    SUNDAY: ResourcesIds.WEEKDAY_SUNDAY,
};

/** One weekday, as the API sends it and as the editor holds it. */
export interface IProgramDay {
    dayOfWeek: Weekday;
    open: boolean;
    /** `HH:mm`, the way an `<input type="time">` reads it. */
    opensAt: string;
    closesAt: string;
}

/** What GET /clinic-program returns. Always all seven days. */
export interface IClinicProgram {
    days: IProgramDay[];
    /**
     * Whose own hours a save had to cut back, by name. Present only on the response to
     * PUT /clinic-program, and empty there unless somebody was actually narrowed.
     *
     * A therapist's program can never exceed the clinic's, so shortening a day here shortens
     * it for whoever worked past the new closing time. The API does that narrowing and names
     * the people it happened to, so the screen can say so rather than leaving an administrator
     * to hear it from the therapist.
     */
    narrowedTherapists?: string[];
}

/** What PUT /clinic-program takes. A full replace of the week, all seven days. */
export interface ClinicProgramRequest {
    days: IProgramDay[];
}

/** What the editor holds: the seven days in `WEEKDAYS` order. */
export type ClinicProgramDraft = IProgramDay[];

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

const DEFAULT_OPENS_AT = '08:00';
const DEFAULT_CLOSES_AT = '19:00';

/** The days a clinic is open unless it says otherwise. Matches the API's own defaults. */
const DEFAULT_OPEN_DAYS: Weekday[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];

/** What the editor opens on before the first load lands. Matches the API's own defaults. */
export const defaultClinicProgramDraft = (): ClinicProgramDraft =>
    WEEKDAYS.map(dayOfWeek => ({
        dayOfWeek,
        open: DEFAULT_OPEN_DAYS.includes(dayOfWeek),
        opensAt: DEFAULT_OPENS_AT,
        closesAt: DEFAULT_CLOSES_AT,
    }));

/**
 * The response as seven editable rows.
 *
 * Rebuilt in `WEEKDAYS` order rather than rendered in the order it arrived, and any day the
 * response failed to mention is filled from the default -- six rows drawn because a row was
 * missing would be a worse bug than a wrong hour, because nothing about the screen would say
 * so. Times are trimmed to `HH:mm` because `<input type="time">` renders nothing for a value
 * it cannot parse, and a clinic whose row was edited by hand may carry seconds.
 */
export const toClinicProgramDraft = (program?: IClinicProgram): ClinicProgramDraft => {
    if (!program) {
        return defaultClinicProgramDraft();
    }

    const byDay = new Map<Weekday, IProgramDay>(program.days.map(day => [day.dayOfWeek, day]));

    return defaultClinicProgramDraft().map(fallback => {
        const day = byDay.get(fallback.dayOfWeek);

        return day
            ? {
                  dayOfWeek: day.dayOfWeek,
                  open: day.open,
                  opensAt: day.opensAt.slice(0, 5),
                  closesAt: day.closesAt.slice(0, 5),
              }
            : fallback;
    });
};

export const toClinicProgramRequest = (draft: ClinicProgramDraft): ClinicProgramRequest => ({
    days: draft,
});

/** Whether one row's hours make sense. A shut day is exempt from nothing but the reading. */
export const isValidProgramDay = (day: IProgramDay): boolean =>
    TIME_PATTERN.test(day.opensAt) &&
    TIME_PATTERN.test(day.closesAt) &&
    // A plain string comparison is exactly a time comparison for zero-padded 24h values, the
    // same reason `clinicSchema` gives for its own ordering test.
    day.closesAt > day.opensAt;

/*
 * There is deliberately no week-level `isValidClinicProgram` here. `isValidProgramDay` is the
 * rule, and `ClinicAdmin/workingHoursSchema.ts` applies it per row so the message lands on the
 * day at fault -- a week-level answer could only say "something is wrong somewhere".
 *
 * Shut days are checked too, because the API checks them: a closed Saturday still stores real
 * hours, and storing nonsense would hand it back the next time somebody reopened the day.
 */

/**
 * The weekday an ISO date falls on.
 *
 * `2026-03-10` is parsed by `Date` as UTC midnight, so `getUTCDay` reads the intended day
 * whatever the viewer's offset -- the local accessors are what would slide it by one for
 * anybody west of Greenwich. This is the only place in the app a date string meets `Date`,
 * and it is safe only because of that pairing.
 */
export const weekdayOf = (isoDate: string): Weekday =>
    WEEKDAYS[(new Date(`${isoDate}T00:00:00Z`).getUTCDay() + 6) % 7];

/** When the clinic is open on that date, or null when it is shut or nothing is known yet. */
export const openHoursOn = (
    program: IClinicProgram | undefined,
    isoDate: string
): { opensAt: string; closesAt: string } | null => {
    if (!program) {
        return null;
    }

    const day = program.days.find(entry => entry.dayOfWeek === weekdayOf(isoDate));

    return day && day.open ? { opensAt: day.opensAt, closesAt: day.closesAt } : null;
};
